<template>
  <div>
    <HrPageLayout
      title="Trainings Catalog"
      subtitle="Manage training courses and certifications"
      icon="📚"
      :breadcrumb-items="[
        { label: 'Home', route: '/dashboard' },
        { label: 'Training & Development' },
        { label: 'Trainings Catalog' }
      ]"
    >
      <template #header-actions>
        <button type="button" class="hr-btn hr-btn-primary" @click="openAdd">
          <i class="bx bx-plus"></i> Add Training
        </button>
      </template>

      <div class="certifications-content">
        <div v-if="message" class="hr-alert" :class="messageSuccess ? 'hr-alert-success' : 'hr-alert-danger'">
          {{ message }}
          <button type="button" class="hr-btn hr-btn-sm" @click="message = ''">&times;</button>
        </div>

        <div class="hr-chart-card">
          <div class="hr-chart-header hr-flex-between">
            <div class="hr-search-wrap">
              <input v-model="searchKeyword" class="hr-input" type="text" placeholder="Search trainings..." @input="onSearch">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-filter-group" style="display:flex;gap:8px;flex-wrap:wrap;">
              <select v-model="filterType" @change="reloadAll" class="hr-select" style="min-width: 130px;">
                <option value="">All types</option>
                <option value="internal">Internal</option>
                <option value="external">External</option>
                <option value="online">Online</option>
                <option value="certification">Certification</option>
                <option value="workshop">Workshop</option>
                <option value="seminar">Seminar</option>
              </select>
              <select v-model="filterStatus" @change="reloadAll" class="hr-select" style="min-width: 140px;">
                <option value="">All statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="postponed">Postponed</option>
              </select>
            </div>
          </div>

          <div class="catalog-stats">
            <button type="button" class="cstat" :class="{ active: filterStatus === '' }" @click="filterStatus=''; reloadAll()">
              <span>Total sessions</span><strong>{{ scheduleStats.total || 0 }}</strong>
            </button>
            <button type="button" class="cstat cstat-active" :class="{ active: filterStatus === 'active' }" @click="filterStatus='active'; reloadAll()">
              <span>Active</span><strong>{{ scheduleStats.active || 0 }}</strong>
            </button>
            <button type="button" class="cstat cstat-done" :class="{ active: filterStatus === 'completed' }" @click="filterStatus='completed'; reloadAll()">
              <span>Completed</span><strong>{{ scheduleStats.completed || 0 }}</strong>
            </button>
            <button type="button" class="cstat cstat-cancel" :class="{ active: filterStatus === 'cancelled' }" @click="filterStatus='cancelled'; reloadAll()">
              <span>Cancelled</span><strong>{{ scheduleStats.cancelled || 0 }}</strong>
            </button>
            <button type="button" class="cstat cstat-post" :class="{ active: filterStatus === 'postponed' }" @click="filterStatus='postponed'; reloadAll()">
              <span>Postponed</span><strong>{{ scheduleStats.postponed || 0 }}</strong>
            </button>
          </div>

          <div class="hr-chart-body">
            <div v-if="loading" class="hr-loading-wrap">
              <div class="hr-spinner"></div>
              <p class="hr-text-muted">Loading trainings...</p>
            </div>
            <div v-else class="hr-table-wrapper">
              <table class="hr-table hr-table-hover">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Code</th>
                    <th>Name</th>
                    <th>Type</th>
                    <th>Session dates</th>
                    <th>Audience</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(t, idx) in catalogRows" :key="(t.schedule_id || t.id || t.trainingID) + '-' + idx">
                    <td>{{ idx + 1 }}</td>
                    <td>{{ t.trainingCode }}</td>
                    <td>{{ t.trainingName }}</td>
                    <td><span class="hr-badge hr-badge-info">{{ t.trainingType }}</span></td>
                    <td>{{ formatRowDates(t) }}</td>
                    <td>{{ formatRowAudience(t) }}</td>
                    <td>
                      <span class="hr-badge" :class="statusBadge(t.workflow_status || t.is_active)">
                        {{ statusLabel(t.workflow_status, t.is_active) }}
                      </span>
                    </td>
                    <td class="actions-cell">
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-primary" title="Edit" @click="openEditFromRow(t)"><i class="bx bx-edit"></i></button>
                      <template v-if="t.schedule_id || t.id">
                        <button
                          v-if="(t.workflow_status || 'active') === 'active' || (t.workflow_status || 'active') === 'postponed'"
                          type="button" class="hr-btn hr-btn-sm hr-btn-outline-success" title="Mark done"
                          @click="setStatus(t, 'completed')"
                        >Done</button>
                        <button
                          v-if="(t.workflow_status || 'active') !== 'cancelled'"
                          type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" title="Cancel training"
                          @click="setStatus(t, 'cancelled')"
                        >Cancel</button>
                        <button
                          v-if="(t.workflow_status || 'active') !== 'completed' && (t.workflow_status || 'active') !== 'cancelled'"
                          type="button" class="hr-btn hr-btn-sm hr-btn-outline-warning" title="Postpone"
                          @click="openPostpone(t)"
                        >Postpone</button>
                      </template>
                      <button type="button" class="hr-btn hr-btn-sm hr-btn-outline-danger" title="Delete course" @click="confirmDelete(t)"><i class="bx bx-trash"></i></button>
                    </td>
                  </tr>
                  <tr v-if="catalogRows.length === 0">
                    <td colspan="8" class="hr-text-center hr-py-4 hr-text-muted">No trainings / sessions found</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </HrPageLayout>

    <!-- Postpone modal -->
    <div class="hr-modal-overlay" v-if="postponeTarget" @click.self="postponeTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header">
          <h3>Postpone training</h3>
          <button type="button" class="hr-modal-close" @click="postponeTarget = null">&times;</button>
        </div>
        <div class="hr-p-3">
          <p class="hr-text-muted hr-small">{{ postponeTarget.trainingName }}</p>
          <label class="hr-form-label">New start date</label>
          <input v-model="postponeForm.start_date" type="date" class="hr-input hr-mb-2">
          <label class="hr-form-label">New end date</label>
          <input v-model="postponeForm.end_date" type="date" class="hr-input hr-mb-2">
          <label class="hr-form-label">Note (optional)</label>
          <textarea v-model="postponeForm.note" class="hr-textarea" rows="2"></textarea>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="postponeTarget = null">Close</button>
            <button type="button" class="hr-btn hr-btn-primary" :disabled="statusSaving" @click="confirmPostpone">Save new period</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div class="hr-modal-overlay" v-if="showModal" @click.self="showModal = false">
      <div class="hr-modal hr-modal-large">
        <div class="hr-modal-header">
          <h3>{{ editingId ? 'Edit Training' : 'Add Training' }}</h3>
          <button type="button" class="hr-modal-close" @click="showModal = false">&times;</button>
        </div>
        <form @submit.prevent="saveTraining" class="hr-form-grid hr-p-3">
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Code</label>
            <input v-model="form.trainingCode" class="hr-input" placeholder="e.g. TR-SAFETY01">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Training Name <span class="hr-text-danger">*</span></label>
            <input v-model="form.trainingName" class="hr-input" required placeholder="Training name">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Type</label>
            <select v-model="form.trainingType" class="hr-select">
              <option value="internal">Internal</option>
              <option value="external">External</option>
              <option value="online">Online</option>
              <option value="certification">Certification</option>
              <option value="workshop">Workshop</option>
              <option value="seminar">Seminar</option>
            </select>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Category</label>
            <input v-model="form.category" class="hr-input" placeholder="Category">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Provider</label>
            <input v-model="form.provider" class="hr-input" placeholder="Provider">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Duration (days / hours)</label>
            <div class="hr-flex gap-2">
              <input v-model.number="form.duration_days" class="hr-input" type="number" min="0" placeholder="Days">
              <input v-model.number="form.duration_hours" class="hr-input" type="number" min="0" placeholder="Hours">
            </div>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Cost</label>
            <input v-model.number="form.cost" class="hr-input" type="number" step="0.01" min="0" placeholder="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Currency</label>
            <input v-model="form.currency" class="hr-input" placeholder="KES">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Description</label>
            <textarea v-model="form.description" class="hr-textarea" rows="2" placeholder="Description"></textarea>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Mandatory</label>
            <input type="checkbox" v-model="form.is_mandatory" :true-value="1" :false-value="0">
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Active</label>
            <input type="checkbox" v-model="form.is_active" :true-value="1" :false-value="0">
          </div>

          <div class="hr-filter-group span-full schedule-section">
            <h4 class="schedule-heading">Schedule &amp; audience</h4>
            <p class="hr-text-muted hr-small">Set dates so this training appears on the Training Calendar and in My Training.</p>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">Start date <span class="hr-text-danger">*</span></label>
            <input v-model="form.start_date" class="hr-input" type="date" required>
          </div>
          <div class="hr-filter-group span-half">
            <label class="hr-form-label">End date <span class="hr-text-danger">*</span></label>
            <input v-model="form.end_date" class="hr-input" type="date" required>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Session venue</label>
            <input v-model="form.venue" class="hr-input" placeholder="e.g. County HQ boardroom">
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Audience</label>
            <select v-model="form.audience_mode" class="hr-select">
              <option value="all">All employees</option>
              <option value="departments">Specific departments</option>
            </select>
          </div>
          <div v-if="form.audience_mode === 'departments'" class="hr-filter-group span-full dept-pick">
            <label class="hr-form-label">Departments <span class="hr-text-danger">*</span></label>
            <div class="dept-check-grid">
              <label v-for="d in departments" :key="d.id" class="dept-check">
                <input type="checkbox" :value="Number(d.id)" v-model="form.department_ids">
                {{ d.departmentName }}
              </label>
            </div>
          </div>
          <div class="hr-filter-group span-full">
            <label class="hr-form-label">Session notes</label>
            <textarea v-model="form.schedule_notes" class="hr-textarea" rows="2" placeholder="Optional instructions for attendees"></textarea>
          </div>

          <div class="hr-modal-footer span-full">
            <button type="button" class="hr-btn hr-btn-secondary" @click="showModal = false">Cancel</button>
            <button type="submit" class="hr-btn hr-btn-primary" :disabled="saving">
              <span v-if="saving" class="hr-spinner small"></span>
              <i v-else class="bx bx-save"></i> Save
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete confirm -->
    <div class="hr-modal-overlay" v-if="deleteTarget" @click.self="deleteTarget = null">
      <div class="hr-modal hr-modal-small">
        <div class="hr-modal-header">
          <h3>Delete Training</h3>
          <button type="button" class="hr-modal-close" @click="deleteTarget = null">&times;</button>
        </div>
        <div class="hr-p-3">
          <p>Delete "{{ deleteTarget && deleteTarget.trainingName }}"? This cannot be undone.</p>
          <div class="hr-mt-3 hr-flex gap-2">
            <button type="button" class="hr-btn hr-btn-secondary" @click="deleteTarget = null">Cancel</button>
            <button type="button" class="hr-btn hr-btn-danger" @click="doDelete" :disabled="deleting">Delete</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: "TrainingsCatalog",
  components: { HrPageLayout },
  data() {
    return {
      trainings: [],
      catalogRows: [],
      scheduleStats: { active: 0, completed: 0, cancelled: 0, postponed: 0, total: 0 },
      loading: false,
      message: '',
      messageSuccess: false,
      searchKeyword: '',
      filterType: '',
      filterStatus: '',
      showModal: false,
      editingId: 0,
      scheduleId: 0,
      saving: false,
      statusSaving: false,
      departments: [],
      scheduleMap: {},
      searchTimer: null,
      postponeTarget: null,
      postponeForm: { start_date: '', end_date: '', note: '' },
      form: {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1,
        start_date: '',
        end_date: '',
        venue: '',
        audience_mode: 'all',
        department_ids: [],
        schedule_notes: ''
      },
      deleteTarget: null,
      deleting: false
    };
  },
  mounted() {
    this.loadDepartments();
    this.reloadAll();
  },
  methods: {
    onSearch() {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => this.reloadAll(), 300);
    },
    reloadAll() {
      this.loadCatalogSchedules();
    },
    statusLabel(ws, isActive) {
      const s = (ws || '').toLowerCase();
      if (s) return s.charAt(0).toUpperCase() + s.slice(1);
      return isActive == 1 ? 'Active' : 'Inactive';
    },
    statusBadge(ws) {
      const s = String(ws || 'active').toLowerCase();
      if (s === 'completed') return 'hr-badge-success';
      if (s === 'cancelled') return 'hr-badge-danger';
      if (s === 'postponed') return 'hr-badge-warning';
      return 'hr-badge-info';
    },
    formatRowDates(t) {
      if (t.start_date) {
        return t.start_date === t.end_date ? t.start_date : `${t.start_date} – ${t.end_date || ''}`;
      }
      return this.formatSessionDates(t);
    },
    formatRowAudience(t) {
      if (t.audience_mode) {
        if (t.audience_mode === 'all') return 'All employees';
        const names = (t.departments || []).map(d => d.departmentName).filter(Boolean);
        return names.length ? names.join(', ') : 'Departments';
      }
      return this.formatAudience(t);
    },
    async loadCatalogSchedules() {
      this.loading = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "listTrainingCatalogSchedules");
      if (this.filterType) data.append("trainingType", this.filterType);
      if (this.filterStatus) data.append("workflow_status", this.filterStatus);
      if (this.searchKeyword) data.append("search", this.searchKeyword.trim());
      try {
        const res = await execute(data, authUrl);
        if (res.data?.success) {
          const list = res.data.data?.schedules || [];
          this.scheduleStats = res.data.data?.stats || this.scheduleStats;
          // Map schedule rows for catalog; also keep trainings without schedules via getTrainings if empty status filter
          this.catalogRows = list.map((s) => ({
            ...s,
            schedule_id: s.id,
            trainingID: s.training_id || s.trainingID
          }));
          // cache schedules for edit
          const map = {};
          list.forEach((s) => {
            const tid = s.training_id || s.trainingID;
            if (tid && !map[tid]) map[tid] = s;
          });
          this.scheduleMap = map;
          if (!list.length) {
            await this.loadTrainingsFallback();
          }
        } else {
          await this.loadTrainingsFallback();
        }
      } catch (e) {
        this.message = e.message || 'Failed to load trainings';
        this.messageSuccess = false;
        await this.loadTrainingsFallback();
      } finally {
        this.loading = false;
      }
    },
    async loadTrainingsFallback() {
      const data = new FormData();
      data.append("function", "getTrainings");
      if (this.filterType) data.append("trainingType", this.filterType);
      data.append("activeOnly", "0");
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          let list = res.data.data.trainings || [];
          const q = (this.searchKeyword || '').toLowerCase();
          if (q) {
            list = list.filter(t =>
              (t.trainingName && t.trainingName.toLowerCase().includes(q)) ||
              (t.trainingCode && t.trainingCode.toLowerCase().includes(q)) ||
              (t.category && t.category.toLowerCase().includes(q))
            );
          }
          this.trainings = list;
          if (!this.catalogRows.length) {
            this.catalogRows = list;
            await this.loadScheduleMap(list);
          }
        }
      } catch (_) { /* ignore */ }
    },
    openEditFromRow(t) {
      if (t.trainingID || t.training_id) {
        const course = {
          trainingID: t.trainingID || t.training_id,
          trainingCode: t.trainingCode,
          trainingName: t.trainingName,
          trainingType: t.trainingType,
          category: t.category,
          provider: t.provider,
          duration_days: t.duration_days,
          duration_hours: t.duration_hours,
          cost: t.cost,
          currency: t.currency,
          is_mandatory: t.is_mandatory,
          is_active: t.training_is_active != null ? t.training_is_active : t.is_active
        };
        if (t.schedule_id || t.id) {
          this.scheduleMap[course.trainingID] = t;
        }
        this.openEdit(course);
      }
    },
    openPostpone(t) {
      this.postponeTarget = t;
      this.postponeForm = {
        start_date: t.start_date || '',
        end_date: t.end_date || '',
        note: ''
      };
    },
    async confirmPostpone() {
      await this.setStatus(this.postponeTarget, 'postponed', this.postponeForm);
      this.postponeTarget = null;
    },
    async setStatus(t, status, extra = {}) {
      const sid = t.schedule_id || t.id;
      if (!sid) {
        this.message = 'No schedule session on this training yet.';
        this.messageSuccess = false;
        return;
      }
      if (status === 'cancelled' && !window.confirm('Cancel this training session?')) return;
      if (status === 'completed' && !window.confirm('Mark this training as completed / done?')) return;
      this.statusSaving = true;
      const fd = new FormData();
      fd.append('function', 'updateTrainingScheduleStatus');
      fd.append('scheduleID', String(sid));
      fd.append('workflow_status', status);
      fd.append('status_note', extra.note || '');
      fd.append('updatedBy', sessionStorage.getItem('sessionNames') || '');
      if (status === 'postponed') {
        fd.append('start_date', extra.start_date || '');
        fd.append('end_date', extra.end_date || '');
      }
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.message = res.data.message || 'Status updated';
          this.messageSuccess = true;
          this.reloadAll();
        } else {
          this.message = res.data?.message || 'Status update failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Status update failed';
        this.messageSuccess = false;
      } finally {
        this.statusSaving = false;
      }
    },
    async loadDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      try {
        const res = await execute(data, authUrl);
        const payload = res?.data?.data;
        this.departments = Array.isArray(payload?.departments) ? payload.departments : (Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (_) {
        this.departments = [];
      }
    },
    defaultForm() {
      return {
        trainingCode: '',
        trainingName: '',
        trainingType: 'internal',
        category: '',
        provider: '',
        duration_days: null,
        duration_hours: null,
        cost: null,
        currency: 'KES',
        description: '',
        is_mandatory: 0,
        is_active: 1,
        start_date: '',
        end_date: '',
        venue: '',
        audience_mode: 'all',
        department_ids: [],
        schedule_notes: ''
      };
    },
    formatSessionDates(t) {
      const s = this.scheduleMap[t.trainingID];
      if (!s) return '—';
      return s.start_date === s.end_date ? s.start_date : `${s.start_date} – ${s.end_date}`;
    },
    formatAudience(t) {
      const s = this.scheduleMap[t.trainingID];
      if (!s) return '—';
      if (s.audience_mode === 'all') return 'All employees';
      const names = (s.departments || []).map(d => d.departmentName).filter(Boolean);
      return names.length ? names.join(', ') : 'Departments';
    },
    async loadTrainings() {
      return this.loadTrainingsFallback();
    },
    async loadScheduleMap(list) {
      const map = {};
      for (const t of list) {
        const fd = new FormData();
        fd.append("function", "getTrainingSchedulesForTraining");
        fd.append("trainingID", t.trainingID);
        try {
          const res = await execute(fd, authUrl);
          const schedules = res.data?.data?.schedules || [];
          if (schedules.length) map[t.trainingID] = schedules[0];
        } catch (_) { /* ignore */ }
      }
      this.scheduleMap = map;
    },
    openAdd() {
      this.editingId = 0;
      this.scheduleId = 0;
      this.form = this.defaultForm();
      this.showModal = true;
    },
    async openEdit(t) {
      this.editingId = t.trainingID;
      this.scheduleId = 0;
      this.form = {
        ...this.defaultForm(),
        trainingCode: t.trainingCode || '',
        trainingName: t.trainingName || '',
        trainingType: t.trainingType || 'internal',
        category: t.category || '',
        provider: t.provider || '',
        duration_days: t.duration_days ?? null,
        duration_hours: t.duration_hours ?? null,
        cost: t.cost != null ? t.cost : null,
        currency: t.currency || 'KES',
        description: t.description || '',
        is_mandatory: t.is_mandatory ?? 0,
        is_active: t.is_active != null ? t.is_active : 1
      };
      const sched = this.scheduleMap[t.trainingID];
      if (sched) {
        this.applyScheduleToForm(sched);
      } else {
        await this.loadScheduleForEdit(t.trainingID);
      }
      this.showModal = true;
    },
    applyScheduleToForm(sched) {
      this.scheduleId = sched.id || sched.schedule_id || 0;
      this.form.start_date = sched.start_date || '';
      this.form.end_date = sched.end_date || '';
      this.form.venue = sched.venue || '';
      this.form.audience_mode = sched.audience_mode || 'all';
      this.form.schedule_notes = sched.notes || '';
      this.form.department_ids = (sched.departments || []).map(d => Number(d.department_id)).filter(n => n > 0);
    },
    async loadScheduleForEdit(trainingID) {
      const fd = new FormData();
      fd.append("function", "getTrainingSchedulesForTraining");
      fd.append("trainingID", trainingID);
      try {
        const res = await execute(fd, authUrl);
        const schedules = res.data?.data?.schedules || [];
        if (schedules.length) this.applyScheduleToForm(schedules[0]);
      } catch (_) { /* ignore */ }
    },
    async saveTraining() {
      if (!this.form.start_date || !this.form.end_date) {
        this.message = 'Start and end dates are required.';
        this.messageSuccess = false;
        return;
      }
      if (this.form.start_date > this.form.end_date) {
        this.message = 'End date must be on or after start date.';
        this.messageSuccess = false;
        return;
      }
      if (this.form.audience_mode === 'departments' && (!this.form.department_ids || !this.form.department_ids.length)) {
        this.message = 'Select at least one department.';
        this.messageSuccess = false;
        return;
      }
      this.saving = true;
      this.message = '';
      const data = new FormData();
      data.append("function", "saveTraining");
      data.append("trainingID", this.editingId);
      data.append("trainingCode", this.form.trainingCode);
      data.append("trainingName", this.form.trainingName);
      data.append("trainingType", this.form.trainingType);
      data.append("category", this.form.category);
      data.append("provider", this.form.provider);
      data.append("duration_days", this.form.duration_days || 0);
      data.append("duration_hours", this.form.duration_hours || 0);
      if (this.form.cost != null && this.form.cost !== '') data.append("cost", this.form.cost);
      data.append("currency", this.form.currency);
      data.append("description", this.form.description);
      data.append("is_mandatory", this.form.is_mandatory ? '1' : '0');
      data.append("is_active", this.form.is_active ? '1' : '0');
      data.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          const trainingID = res.data.data?.trainingID || this.editingId;
          const schedOk = await this.saveSchedule(trainingID);
          if (!schedOk) {
            this.message = this.message || 'Training saved but schedule failed.';
            this.messageSuccess = false;
            return;
          }
          this.message = res.data.message || 'Saved successfully';
          this.messageSuccess = true;
          this.showModal = false;
          this.reloadAll();
        } else {
          this.message = res.data.message || 'Save failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Save failed';
        this.messageSuccess = false;
      } finally {
        this.saving = false;
      }
    },
    async saveSchedule(trainingID) {
      const fd = new FormData();
      fd.append("function", "saveTrainingSchedule");
      if (this.scheduleId) fd.append("scheduleID", this.scheduleId);
      fd.append("trainingID", trainingID);
      fd.append("start_date", this.form.start_date);
      fd.append("end_date", this.form.end_date);
      fd.append("venue", this.form.venue || '');
      fd.append("audience_mode", this.form.audience_mode);
      fd.append("notes", this.form.schedule_notes || '');
      fd.append("department_ids", JSON.stringify(this.form.department_ids || []));
      fd.append("createdBy", sessionStorage.getItem("sessionNames") || '');
      try {
        const res = await execute(fd, authUrl);
        if (res.data?.success) {
          this.scheduleId = res.data.data?.scheduleID || this.scheduleId;
          return true;
        }
        this.message = res.data?.message || 'Schedule save failed';
        return false;
      } catch (e) {
        this.message = e.message || 'Schedule save failed';
        return false;
      }
    },
    confirmDelete(t) {
      this.deleteTarget = t;
    },
    async doDelete() {
      if (!this.deleteTarget) return;
      this.deleting = true;
      const data = new FormData();
      data.append("function", "deleteTraining");
      data.append("trainingID", this.deleteTarget.trainingID);
      try {
        const res = await execute(data, authUrl);
        if (res.data.success) {
          this.message = 'Training deleted';
          this.messageSuccess = true;
          this.deleteTarget = null;
          this.reloadAll();
        } else {
          this.message = res.data.message || 'Delete failed';
          this.messageSuccess = false;
        }
      } catch (e) {
        this.message = e.message || 'Delete failed';
        this.messageSuccess = false;
      } finally {
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.certifications-content { padding: 0; }
.catalog-stats {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 0.65rem;
  padding: 0.75rem 1rem 0;
}
.cstat {
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  text-align: left;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}
.cstat span { font-size: 0.7rem; font-weight: 700; color: #64748b; text-transform: uppercase; }
.cstat strong { font-size: 1.2rem; color: #0f172a; }
.cstat.active { outline: 2px solid #0c4a6e; background: #fff; }
.cstat-active strong { color: #0284c7; }
.cstat-done strong { color: #059669; }
.cstat-cancel strong { color: #dc2626; }
.cstat-post strong { color: #d97706; }
.actions-cell { display: flex; flex-wrap: wrap; gap: 4px; }
.hr-badge-warning { background: #fef3c7; color: #92400e; }
.hr-alert { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-radius: 12px; margin-bottom: 16px; }
.hr-alert-success { background: #d1fae5; color: #065f46; }
.hr-alert-danger { background: #fee2e2; color: #991b1b; }
.hr-flex.gap-2 { display: flex; gap: 8px; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.4); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.hr-modal { background: #fff; border-radius: 16px; max-width: 96vw; max-height: 90vh; overflow-y: auto; }
.hr-modal-medium { width: 560px; }
.hr-modal-large { width: 720px; }
.schedule-heading { margin: 0 0 4px; font-size: 1rem; color: #0f172a; }
.schedule-section { border-top: 1px solid #e2e8f0; padding-top: 12px; margin-top: 4px; }
.hr-small { font-size: 0.875rem; }
.dept-check-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 8px; max-height: 160px; overflow-y: auto; padding: 8px; border: 1px solid #e2e8f0; border-radius: 8px; }
.dept-check { display: flex; align-items: center; gap: 8px; font-size: 0.9rem; margin: 0; }
.hr-modal-small { width: 400px; }
.hr-modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-close { background: none; border: none; font-size: 24px; cursor: pointer; color: #64748b; }
.hr-modal-footer { display: flex; gap: 12px; justify-content: flex-end; margin-top: 16px; }
.span-half { grid-column: span 1; }
.span-full { grid-column: 1 / -1; }
.hr-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hr-p-3 { padding: 20px; }
.hr-mt-3 { margin-top: 16px; }
.hr-mb-2 { margin-bottom: 0.5rem; }
@media (max-width: 900px) {
  .catalog-stats { grid-template-columns: 1fr 1fr; }
}
</style>
